"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.applyScopeDataToEvent = applyScopeDataToEvent;
exports.mergeAndOverwriteScopeData = mergeAndOverwriteScopeData;
exports.mergeScopeData = mergeScopeData;

var _dynamicSamplingContext = require("../tracing/dynamicSamplingContext.js");

var _object = require("../utils-hoist/object.js");

var _merge = require("./merge.js");

var _spanUtils = require("./spanUtils.js");

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

/**
 * Applies data from the scope to the event and runs all event processors on it.
 */
function applyScopeDataToEvent(event, data) {
  const fingerprint = data.fingerprint,
        span = data.span,
        breadcrumbs = data.breadcrumbs,
        sdkProcessingMetadata = data.sdkProcessingMetadata; // Apply general data

  applyDataToEvent(event, data); // We want to set the trace context for normal events only if there isn't already
  // a trace context on the event. There is a product feature in place where we link
  // errors with transaction and it relies on that.

  if (span) {
    applySpanToEvent(event, span);
  }

  applyFingerprintToEvent(event, fingerprint);
  applyBreadcrumbsToEvent(event, breadcrumbs);
  applySdkMetadataToEvent(event, sdkProcessingMetadata);
}
/** Merge data of two scopes together. */


function mergeScopeData(data, mergeData) {
  const extra = mergeData.extra,
        tags = mergeData.tags,
        user = mergeData.user,
        contexts = mergeData.contexts,
        level = mergeData.level,
        sdkProcessingMetadata = mergeData.sdkProcessingMetadata,
        breadcrumbs = mergeData.breadcrumbs,
        fingerprint = mergeData.fingerprint,
        eventProcessors = mergeData.eventProcessors,
        attachments = mergeData.attachments,
        propagationContext = mergeData.propagationContext,
        transactionName = mergeData.transactionName,
        span = mergeData.span;
  mergeAndOverwriteScopeData(data, 'extra', extra);
  mergeAndOverwriteScopeData(data, 'tags', tags);
  mergeAndOverwriteScopeData(data, 'user', user);
  mergeAndOverwriteScopeData(data, 'contexts', contexts);
  data.sdkProcessingMetadata = (0, _merge.merge)(data.sdkProcessingMetadata, sdkProcessingMetadata, 2);

  if (level) {
    data.level = level;
  }

  if (transactionName) {
    data.transactionName = transactionName;
  }

  if (span) {
    data.span = span;
  }

  if (breadcrumbs.length) {
    data.breadcrumbs = [...data.breadcrumbs, ...breadcrumbs];
  }

  if (fingerprint.length) {
    data.fingerprint = [...data.fingerprint, ...fingerprint];
  }

  if (eventProcessors.length) {
    data.eventProcessors = [...data.eventProcessors, ...eventProcessors];
  }

  if (attachments.length) {
    data.attachments = [...data.attachments, ...attachments];
  }

  data.propagationContext = _objectSpread(_objectSpread({}, data.propagationContext), propagationContext);
}
/**
 * Merges certain scope data. Undefined values will overwrite any existing values.
 * Exported only for tests.
 */


function mergeAndOverwriteScopeData(data, prop, mergeVal) {
  data[prop] = (0, _merge.merge)(data[prop], mergeVal, 1);
}

function applyDataToEvent(event, data) {
  const extra = data.extra,
        tags = data.tags,
        user = data.user,
        contexts = data.contexts,
        level = data.level,
        transactionName = data.transactionName;
  const cleanedExtra = (0, _object.dropUndefinedKeys)(extra);

  if (cleanedExtra && Object.keys(cleanedExtra).length) {
    event.extra = _objectSpread(_objectSpread({}, cleanedExtra), event.extra);
  }

  const cleanedTags = (0, _object.dropUndefinedKeys)(tags);

  if (cleanedTags && Object.keys(cleanedTags).length) {
    event.tags = _objectSpread(_objectSpread({}, cleanedTags), event.tags);
  }

  const cleanedUser = (0, _object.dropUndefinedKeys)(user);

  if (cleanedUser && Object.keys(cleanedUser).length) {
    event.user = _objectSpread(_objectSpread({}, cleanedUser), event.user);
  }

  const cleanedContexts = (0, _object.dropUndefinedKeys)(contexts);

  if (cleanedContexts && Object.keys(cleanedContexts).length) {
    event.contexts = _objectSpread(_objectSpread({}, cleanedContexts), event.contexts);
  }

  if (level) {
    event.level = level;
  } // transaction events get their `transaction` from the root span name


  if (transactionName && event.type !== 'transaction') {
    event.transaction = transactionName;
  }
}

function applyBreadcrumbsToEvent(event, breadcrumbs) {
  const mergedBreadcrumbs = [...(event.breadcrumbs || []), ...breadcrumbs];
  event.breadcrumbs = mergedBreadcrumbs.length ? mergedBreadcrumbs : undefined;
}

function applySdkMetadataToEvent(event, sdkProcessingMetadata) {
  event.sdkProcessingMetadata = _objectSpread(_objectSpread({}, event.sdkProcessingMetadata), sdkProcessingMetadata);
}

function applySpanToEvent(event, span) {
  event.contexts = _objectSpread({
    trace: (0, _spanUtils.spanToTraceContext)(span)
  }, event.contexts);
  event.sdkProcessingMetadata = _objectSpread({
    dynamicSamplingContext: (0, _dynamicSamplingContext.getDynamicSamplingContextFromSpan)(span)
  }, event.sdkProcessingMetadata);
  const rootSpan = (0, _spanUtils.getRootSpan)(span);
  const transactionName = (0, _spanUtils.spanToJSON)(rootSpan).description;

  if (transactionName && !event.transaction && event.type === 'transaction') {
    event.transaction = transactionName;
  }
}
/**
 * Applies fingerprint from the scope to the event if there's one,
 * uses message if there's one instead or get rid of empty fingerprint
 */


function applyFingerprintToEvent(event, fingerprint) {
  // Make sure it's an array first and we actually have something in place
  event.fingerprint = event.fingerprint ? Array.isArray(event.fingerprint) ? event.fingerprint : [event.fingerprint] : []; // If we have something on the scope, then merge it with event

  if (fingerprint) {
    event.fingerprint = event.fingerprint.concat(fingerprint);
  } // If we have no data at all, remove empty array default


  if (event.fingerprint && !event.fingerprint.length) {
    delete event.fingerprint;
  }
}